import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { enqueueSnackbar } from 'notistack';

import { colors } from '@src/styles';
import { fadeInUpSX, slideInRightSX } from '@src/styles/animations';
import { CanonicalResourceSchema } from './CanonicalResourceSchema';
import { useCanonicalsContext } from '../CanonicalsContext';
import {
  formatCanonicalDate,
  monoFontFamily,
  monoLabelSX,
  sectionCardSX,
  titleFontFamily,
  tonePillSX,
} from './canonicalStyles';

const iconButtonSX = {
  width: 32,
  height: 32,
  color: colors.brand.textSecondary,
  '&:hover': { color: colors.primary.white, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
} as const;

const copyToClipboard = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
    enqueueSnackbar(`${label} copiado`, { variant: 'success', autoHideDuration: 1000 });
  } catch (error) {
    console.error('Failed to copy:', error);
  }
};

const MetaRow = ({ label, value, copyLabel }: { label: string; value?: string; copyLabel?: string }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      minHeight: 36,
      paddingX: 1.5,
      borderBottom: `1px solid ${colors.brand.line}`,
      '&:last-of-type': { borderBottom: 'none' },
      minWidth: 0,
    }}
  >
    <Typography component="dt" sx={{ ...monoLabelSX, width: 128, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box
      component="dd"
      sx={{
        margin: 0,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
      }}
    >
      <Typography
        noWrap
        title={value}
        sx={{
          fontFamily: monoFontFamily,
          fontSize: '12px',
          color: value ? colors.brand.textStrong : colors.brand.textMuted,
        }}
      >
        {value || '—'}
      </Typography>
      {value && copyLabel && (
        <Tooltip title={`Copiar ${copyLabel}`} arrow>
          <IconButton
            size="small"
            aria-label={`Copiar ${copyLabel}`}
            onClick={() => copyToClipboard(value, copyLabel)}
            sx={{ ...iconButtonSX, width: 26, height: 26 }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Box>
);

export const CanonicalDetails = () => {
  const { selectedCanonical, handleEdit, handleCloseDetails } = useCanonicalsContext();

  if (!selectedCanonical) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        minHeight: 0,
        overflowY: { xs: 'auto', md: 'hidden' },
        overflowX: 'hidden',
        paddingBottom: 1,
        ...slideInRightSX,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
        <Tooltip title="Voltar para a lista" arrow>
          <IconButton onClick={handleCloseDetails} aria-label="Voltar para a lista" sx={iconButtonSX}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={monoLabelSX}>Automação canônica</Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: monoFontFamily,
              fontSize: '20px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              color: colors.primary.white,
              wordBreak: 'break-word',
              marginTop: '2px',
            }}
          >
            {selectedCanonical.key}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginTop: 1 }}>
            {selectedCanonical.resourceId && <Box sx={tonePillSX(colors.primary.blue)}>Schema</Box>}
            {selectedCanonical.tarkenExtensionId && <Box sx={tonePillSX(colors.status.concluded)}>Extension</Box>}
          </Box>
        </Box>
        <Tooltip title="Edição indisponível no momento" arrow>
          <span>
            <IconButton
              onClick={() => handleEdit(selectedCanonical)}
              aria-label="Editar recurso"
              disabled
              sx={{ ...iconButtonSX, '&.Mui-disabled': { color: colors.brand.textMuted, opacity: 0.5 } }}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flexShrink: 0, ...fadeInUpSX(60) }}>
        <Box sx={{ ...sectionCardSX, flex: '1 1 320px', minWidth: 0, padding: '14px 16px' }}>
          <Typography sx={{ ...monoLabelSX, marginBottom: 1 }}>Descrição</Typography>
          <Typography
            sx={{
              fontFamily: titleFontFamily,
              fontSize: '13.5px',
              lineHeight: 1.6,
              color: selectedCanonical.description ? colors.brand.textStrong : colors.brand.textMuted,
            }}
          >
            {selectedCanonical.description || 'Nenhuma descrição'}
          </Typography>
        </Box>

        <Box component="dl" sx={{ ...sectionCardSX, flex: '1 1 380px', minWidth: 0, margin: 0, overflow: 'hidden' }}>
          <MetaRow label="Extension ID" value={selectedCanonical.tarkenExtensionId} copyLabel="Extension ID" />
          <MetaRow label="Resource ID" value={selectedCanonical.resourceId} copyLabel="Resource ID" />
          <MetaRow label="Criado em" value={formatCanonicalDate(selectedCanonical.createdAt)} />
          {selectedCanonical.updatedAt && (
            <MetaRow label="Atualizado em" value={formatCanonicalDate(selectedCanonical.updatedAt)} />
          )}
        </Box>
      </Box>

      {selectedCanonical.resourceId && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: { xs: '0 0 auto', md: '1 1 auto' },
            minHeight: 0,
            ...fadeInUpSX(120),
          }}
        >
          <CanonicalResourceSchema />
        </Box>
      )}
    </Box>
  );
};
